/**
 * NavigatorX Voice Analytics Dashboard
 *
 * Voice cloning usage analytics and performance metrics:
 * - Voice clone creation funnel (pending -> recording -> processing -> ready)
 * - Relationship type distribution
 * - Paid vs unpaid voice breakdown
 * - Quality score distribution
 * - Voice processing success/failure rates
 * - Active voice usage metrics
 * - Recent voice activity
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Mic,
  Volume2,
  Users,
  DollarSign,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  Star,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getVoiceStats,
  getVoiceClones,
  VOICE_RECORDING_STATUS,
  VOICE_RELATIONSHIPS,
  type VoiceStats as VoiceStatsType,
  type VoiceClone
} from '../../../utils/services/navigatorXVoiceService';
import { timestampToDate } from '../../../utils/services/navigatorXService';

interface VoiceAnalyticsProps {
  embedded?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  'PENDING': 'bg-gray-100 text-gray-700',
  'RECORDING': 'bg-blue-100 text-blue-700',
  'PROCESSING': 'bg-yellow-100 text-yellow-700',
  'READY': 'bg-green-100 text-green-700',
  'FAILED': 'bg-red-100 text-red-700'
};

const STATUS_ICONS: Record<string, typeof Clock> = {
  'PENDING': Clock,
  'RECORDING': Mic,
  'PROCESSING': Loader2,
  'READY': CheckCircle,
  'FAILED': XCircle
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  'WIFE': 'Wife',
  'HUSBAND': 'Husband',
  'GIRLFRIEND': 'Girlfriend',
  'BOYFRIEND': 'Boyfriend',
  'DAUGHTER': 'Daughter',
  'SON': 'Son',
  'MOTHER': 'Mother',
  'FATHER': 'Father',
  'FRIEND': 'Friend',
  'COWORKER': 'Coworker',
  'OTHER': 'Other'
};

export default function VoiceAnalytics({ embedded = false }: VoiceAnalyticsProps) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<VoiceStatsType | null>(null);
  const [voices, setVoices] = useState<VoiceClone[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [voiceStats, allVoices] = await Promise.all([
        getVoiceStats(),
        getVoiceClones({ limitCount: 500 })
      ]);
      setStats(voiceStats);
      setVoices(allVoices);
    } catch (error) {
      console.error('Error loading voice analytics:', error);
      toast.error('Failed to load voice analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
        <p className="text-gray-500">Failed to load voice statistics</p>
        <Button variant="outline" onClick={loadData} className="mt-2">Retry</Button>
      </div>
    );
  }

  const successRate = stats.totalVoices > 0
    ? (stats.readyCount / stats.totalVoices * 100)
    : 0;
  const failureRate = stats.totalVoices > 0
    ? (stats.failedCount / stats.totalVoices * 100)
    : 0;
  const paidRate = stats.totalVoices > 0
    ? (stats.paidVoices / stats.totalVoices * 100)
    : 0;

  // Quality score distribution
  const qualityBuckets = { 'Excellent (80%+)': 0, 'Good (60-79%)': 0, 'Fair (40-59%)': 0, 'Poor (<40%)': 0, 'No Score': 0 };
  voices.forEach(v => {
    if (v.qualityScore === undefined || v.qualityScore === null) {
      qualityBuckets['No Score']++;
    } else if (v.qualityScore >= 0.8) {
      qualityBuckets['Excellent (80%+)']++;
    } else if (v.qualityScore >= 0.6) {
      qualityBuckets['Good (60-79%)']++;
    } else if (v.qualityScore >= 0.4) {
      qualityBuckets['Fair (40-59%)']++;
    } else {
      qualityBuckets['Poor (<40%)']++;
    }
  });

  // Active vs inactive
  const activeVoices = voices.filter(v => v.isActive).length;
  const inactiveVoices = voices.filter(v => !v.isActive).length;

  // Sorted relationships
  const sortedRelationships = Object.entries(stats.byRelationship)
    .sort((a, b) => b[1] - a[1]);

  // Recent voices (last 10)
  const recentVoices = [...voices]
    .sort((a, b) => {
      const aDate = timestampToDate(a.createdAt);
      const bDate = timestampToDate(b.createdAt);
      if (!aDate) return 1;
      if (!bDate) return -1;
      return bDate.getTime() - aDate.getTime();
    })
    .slice(0, 10);

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Voice Analytics</h2>
            <p className="text-gray-500">Voice cloning usage and performance metrics</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Total Voices</p>
                <p className="text-2xl font-bold text-purple-700">{stats.totalVoices}</p>
                <p className="text-xs text-gray-500">{activeVoices} active</p>
              </div>
              <Volume2 className="h-8 w-8 text-purple-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Ready Voices</p>
                <p className="text-2xl font-bold text-green-700">{stats.readyCount}</p>
                <p className="text-xs text-gray-500">{successRate.toFixed(0)}% success rate</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${stats.failedCount > 0 ? 'from-red-50 to-rose-50 border-red-200' : 'from-gray-50 to-gray-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium">Failed</p>
                <p className="text-2xl font-bold text-red-700">{stats.failedCount}</p>
                <p className="text-xs text-gray-500">{failureRate.toFixed(0)}% failure rate</p>
              </div>
              <XCircle className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Avg Quality</p>
                <p className="text-2xl font-bold text-amber-700">{(stats.avgQualityScore * 100).toFixed(0)}%</p>
                <p className="text-xs text-gray-500">{stats.totalCachedPhrases} cached phrases</p>
              </div>
              <Star className="h-8 w-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Funnel + Payment Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Processing Status Funnel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Processing Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.values(stats.byStatus).every(v => v === 0) ? (
              <p className="text-center text-gray-500 py-8">No processing data</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <RechartsBarChart
                  data={Object.entries(stats.byStatus).map(([status, count]) => ({ status, count }))}
                  layout="vertical"
                  margin={{ top: 5, right: 30, bottom: 5, left: 90 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} stroke="#9ca3af" width={85} />
                  <Tooltip
                    formatter={(value: number) => [value, 'Voices']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {Object.keys(stats.byStatus).map((status) => {
                      const colorMap: Record<string, string> = {
                        READY: '#22c55e', FAILED: '#ef4444', PROCESSING: '#eab308',
                        RECORDING: '#3b82f6', PENDING: '#9ca3af',
                      };
                      return <Cell key={status} fill={colorMap[status] || '#9ca3af'} />;
                    })}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Relationship Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Voice Relationships
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedRelationships.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No relationship data</p>
            ) : (
              <div className="space-y-2">
                {sortedRelationships.map(([relationship, count]) => (
                  <div key={relationship} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700">
                      {RELATIONSHIP_LABELS[relationship] || relationship}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{count}</span>
                      <Badge variant="outline" className="text-xs">
                        {stats.totalVoices > 0 ? `${(count / stats.totalVoices * 100).toFixed(0)}%` : '0%'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment + Quality Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Payment Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Payment Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium">Paid Voices</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-green-700">{stats.paidVoices}</span>
                  <p className="text-xs text-gray-500">{paidRate.toFixed(0)}%</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium">Unpaid Voices</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-gray-700">{stats.unpaidVoices}</span>
                  <p className="text-xs text-gray-500">{(100 - paidRate).toFixed(0)}%</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-500" />
                  <span className="text-sm font-medium">Active / Inactive</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-purple-700">{activeVoices}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="text-gray-500">{inactiveVoices}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quality Score Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4" />
              Quality Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.values(qualityBuckets).every(v => v === 0) ? (
              <p className="text-center text-gray-500 py-8">No quality data</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <RechartsBarChart
                  data={Object.entries(qualityBuckets).map(([bucket, count]) => ({ bucket, count }))}
                  layout="vertical"
                  margin={{ top: 5, right: 30, bottom: 5, left: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis type="category" dataKey="bucket" tick={{ fontSize: 10 }} stroke="#9ca3af" width={95} />
                  <Tooltip
                    formatter={(value: number) => [value, 'Voices']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {Object.keys(qualityBuckets).map((bucket) => {
                      const colorMap: Record<string, string> = {
                        'Excellent (80%+)': '#22c55e', 'Good (60-79%)': '#3b82f6',
                        'Fair (40-59%)': '#eab308', 'Poor (<40%)': '#ef4444',
                      };
                      return <Cell key={bucket} fill={colorMap[bucket] || '#9ca3af'} />;
                    })}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Voice Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-purple-500" />
            Recent Voice Clones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentVoices.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No voice clones found</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {recentVoices.map(voice => {
                const createdDate = timestampToDate(voice.createdAt);
                return (
                  <div key={voice.id} className="p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${voice.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <Volume2 className={`h-4 w-4 ${voice.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{voice.name}</span>
                            {voice.nickname && (
                              <span className="text-xs text-gray-400">&ldquo;{voice.nickname}&rdquo;</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{voice.userDisplayName || voice.userEmail || voice.userId.slice(0, 8)}</span>
                            <Badge variant="outline" className="text-xs">{RELATIONSHIP_LABELS[voice.relationship] || voice.relationship}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge className={`text-xs ${STATUS_COLORS[voice.recordingStatus] || 'bg-gray-100'}`}>
                          {voice.recordingStatus}
                        </Badge>
                        <div className="flex items-center gap-2 mt-1">
                          {voice.isPaid && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                          )}
                          {voice.qualityScore !== undefined && (
                            <span className={`text-xs font-medium ${
                              voice.qualityScore >= 0.8 ? 'text-green-600' :
                              voice.qualityScore >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {(voice.qualityScore * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {createdDate ? createdDate.toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                    </div>
                    {voice.lastError && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                        Error: {voice.lastError}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
